/**
 * The pure half of {@link ChatAttachmentTile}: which layers a tile shows for an
 * upload status, and the progress ring's geometry. Kept React-free so the
 * spec pins the rules both clients render.
 */

export type ChatAttachmentStatus = 'uploading' | 'ready' | 'error';

/** The veil's fade-out once the upload lands (opacity only). */
export const CHAT_ATTACHMENT_FADE_MS = 200;

/** The determinate ring — 20px, centred on the 56px tile. */
export const CHAT_ATTACHMENT_RING = { size: 20, stroke: 2.5 } as const;

export interface ChatAttachmentTileState {
  /** The dimming scrim over the preview: on while uploading or failed. */
  veiled: boolean;
  /**
   * `pulse` — no fraction yet: the scrim breathes on the shared skeleton pulse.
   * `ring` — a real fraction: a still scrim with a determinate ring.
   * `alert` — failed: a still, heavier scrim with the retry / alert glyph.
   * `none` — ready: nothing over the preview.
   */
  overlay: 'pulse' | 'ring' | 'alert' | 'none';
  /** The ring's fill, clamped to 0–1 (0 unless `overlay === 'ring'`). */
  fraction: number;
}

export function chatAttachmentTileState(
  status: ChatAttachmentStatus,
  progress?: number | null,
): ChatAttachmentTileState {
  if (status === 'ready') return { veiled: false, overlay: 'none', fraction: 0 };
  if (status === 'error') return { veiled: true, overlay: 'alert', fraction: 0 };
  const known = typeof progress === 'number' && Number.isFinite(progress);
  return known
    ? { veiled: true, overlay: 'ring', fraction: Math.min(1, Math.max(0, progress)) }
    : { veiled: true, overlay: 'pulse', fraction: 0 };
}

/** Ring maths: radius, circumference, and the dash for a fraction. */
export function chatAttachmentRingGeometry(
  fraction: number,
  size: number = CHAT_ATTACHMENT_RING.size,
  stroke: number = CHAT_ATTACHMENT_RING.stroke,
) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const f = Math.min(1, Math.max(0, fraction));
  return { radius, circumference, dashOffset: circumference * (1 - f) };
}
